import { useAuth } from "@dl/web-core-js/auth";
import { Badge, Button, Dialog, Field, Input, MenuButton, Segmented, Select, Switch, toast, useConfirm } from "@dl/web-core-js/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { ArrowDown, ArrowUp, Plus, Star } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AssemblyVersionClient,
  ManufacturingPartClient,
  MARKETS,
  refusalMessage,
  VersionProfileClient,
  type AssemblyVersionDTO,
  type ProductStructure,
} from "../../api/manufacturing";
import { SalesChannelClient } from "../../api/inventory";
import { NexpimClientClient, clientLabel } from "../../api/pricing";
import type { ProductDTO } from "../../api/product";
import { OpenRecord } from "../../components/OpenRecord";
import { PartBomTab } from "../manufacturing/PartBomTab";
import { useReference } from "../manufacturing/PartDetailPage";
import { PartFilesPanel } from "../manufacturing/PartFilesPanel";
import { PackingGrid } from "./PackingGrid";
import { VersionBoxes } from "./VersionBoxes";

/**
 * How Terrebonne boxes a product (design §14): its assembly versions, the
 * product being the hub that links them. The list says, per version, its part,
 * its boxes, when it is used (its rule), whether its packing list is complete,
 * and lets the default, the order, the on/off state change. Below, the chosen
 * version: which box each piece goes in, its boxes, its assembly BOM, its rule
 * and the product's files.
 */
export function AssemblyTab({ draft }: { draft: ProductDTO }) {
  const { t } = useTranslation();
  const { http, canWrite } = useAuth();
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const client = useMemo(() => new AssemblyVersionClient(http), [http]);
  const key = ["manufacturing", "product-structure", draft.id];
  const { data: structure, isLoading } = useQuery({ queryKey: key, queryFn: () => client.ofProduct(draft.id) });
  const versions = structure?.versions ?? [];
  const [chosenId, setChosenId] = useState<number | null>(null);
  const chosen = versions.find((version) => version.id === chosenId) ?? versions[0] ?? null;
  const [adding, setAdding] = useState(false);
  const [asking, setAsking] = useState(false);
  // Kept while moving between versions: comparing CA and US box by box should
  // not send you back to another view. Until one is picked, a version whose
  // packing list is incomplete opens on it.
  const [view, setView] = useState<DetailView | null>(null);
  const readOnly = !canWrite;

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ["manufacturing"] });
  };
  const act = useMutation({
    mutationFn: (action: () => Promise<void>) => action(),
    onSuccess: refresh,
    onError: (failure) => toast.error(refusalMessage(failure, t("error.generic", "Something went wrong."))),
  });
  const move = (version: AssemblyVersionDTO, by: -1 | 1) => {
    const others = versions.filter((one) => !one.is_default);
    const index = others.findIndex((one) => one.id === version.id);
    const target = index + by;
    if (index < 0 || target < 0 || target >= others.length) return;
    const order = others.map((one) => one.id);
    [order[index], order[target]] = [order[target]!, order[index]!];
    const defaults = versions.filter((one) => one.is_default).map((one) => one.id);
    act.mutate(() => client.reorder(draft.id, [...defaults, ...order]));
  };

  if (isLoading) {
    return <p className="px-4 py-3 text-[13px] text-ink-muted lg:px-6">{t("common.loading", "Loading…")}</p>;
  }
  if (draft.type === "BUNDLE") {
    return <p className="px-4 py-3 text-[13px] text-ink-muted lg:px-6">{t("product.manufacturing.none-bundle", "A bundle is made as its items: open an item to see its part.")}</p>;
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <section className="shrink-0 border-b border-line">
        <div className="flex flex-wrap items-center gap-2 px-4 py-2 lg:px-6">
          <h3 className="text-[13px] font-semibold">{t("assembly.versions.title", "Assembly versions")}</h3>
          <span className="text-[12px] text-ink-muted">
            {t("assembly.versions.hint", "Ways of boxing the product. A rule says when a version is used; the default takes everything else.")}
          </span>
          <span className="ml-auto flex items-center gap-2">
            {versions.length > 0 && (
              <Button size="sm" variant="ghost" onClick={() => setAsking(true)}>
                {t("assembly.which.open", "Which version?")}
              </Button>
            )}
            {!readOnly && (
              <Button size="sm" variant="secondary" icon={<Plus className="size-4" />} onClick={() => setAdding(true)}>
                {t("assembly.version.add", "Add a version")}
              </Button>
            )}
          </span>
        </div>
        {versions.length === 0 ? (
          <p className="px-4 pb-3 text-[13px] text-ink-muted lg:px-6">
            {t("assembly.versions.none", "No assembly version yet: add one with the part Terrebonne makes, or let the go-live script link SIGM's 90-{{sku}}.", {
              sku: draft.metadata?.sku ?? "",
            })}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wide text-ink-faint">
                  <th className="w-16 px-4 py-1 font-medium lg:pl-6" />
                  <th className="px-2 py-1 font-medium">{t("assembly.version.code", "Version")}</th>
                  <th className="px-2 py-1 font-medium">{t("column.part.number", "Part")}</th>
                  <th className="px-2 py-1 text-right font-medium">{t("assembly.version.boxes", "Boxes")}</th>
                  <th className="px-2 py-1 font-medium">{t("assembly.version.used-for", "Used for")}</th>
                  <th className="px-2 py-1 font-medium">{t("assembly.version.packing", "Packing")}</th>
                  <th className="px-2 py-1 font-medium">{t("assembly.version.active", "On")}</th>
                  <th className="w-10 px-2 py-1" />
                </tr>
              </thead>
              <tbody>
                {versions.map((version, index) => {
                  const selected = version.id === chosen?.id;
                  const active = version.part_status === "ACTIVE";
                  const others = versions.filter((one) => !one.is_default);
                  const position = others.findIndex((one) => one.id === version.id);
                  return (
                    <tr
                      key={version.id}
                      className={`cursor-pointer border-t border-line ${selected ? "bg-accent-soft" : "hover:bg-surface-sunk"}`}
                      onClick={() => setChosenId(version.id)}
                    >
                      <td className="px-4 py-1.5 lg:pl-6">
                        {!readOnly && !version.is_default && (
                          <span className="flex gap-0.5" onClick={(event) => event.stopPropagation()}>
                            <Button size="sm" variant="ghost" aria-label={t("assembly.version.up", "Try earlier")} disabled={position <= 0}
                              icon={<ArrowUp className="size-3.5" />} onClick={() => move(version, -1)} />
                            <Button size="sm" variant="ghost" aria-label={t("assembly.version.down", "Try later")} disabled={position >= others.length - 1}
                              icon={<ArrowDown className="size-3.5" />} onClick={() => move(version, 1)} />
                          </span>
                        )}
                        {version.is_default && <span className="sr-only">{index}</span>}
                      </td>
                      <td className="px-2 py-1.5">
                        <span className="flex items-center gap-1.5">
                          <span className="font-semibold">{version.code}</span>
                          {version.is_default && (
                            <Badge size="sm" variant="info">
                              <Star className="mr-0.5 inline size-3" />
                              {t("assembly.version.default", "Default")}
                            </Badge>
                          )}
                        </span>
                      </td>
                      <td className="px-2 py-1.5">
                        <span className="flex items-center gap-1">
                          <span className="font-mono text-[12px]">{version.output_part_number}</span>
                          <OpenRecord target={{ kind: "part", id: version.output_part_id, tab: "bom" }} label={version.output_part_number ?? ""} />
                        </span>
                      </td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{version.box_count ?? 1}</td>
                      <td className="px-2 py-1.5 text-[12px]">
                        {version.is_default ? (
                          <span className="text-ink-muted">{t("assembly.version.everything-else", "Everything no rule takes")}</span>
                        ) : version.version_profile_name ? (
                          <Badge size="sm" variant="neutral">{version.version_profile_name}</Badge>
                        ) : (
                          <span className="text-warn">{t("assembly.version.no-rule", "No rule: never chosen")}</span>
                        )}
                      </td>
                      <td className="px-2 py-1.5">
                        {version.is_complete ? (
                          <Badge size="sm" variant="success">{t("assembly.version.complete", "Complete")}</Badge>
                        ) : (
                          <Badge size="sm" variant="warning">
                            {[
                              version.unplaced_pieces ? t("packing.summary.unplaced", "{{n}} in no box", { n: version.unplaced_pieces }) : null,
                              version.over_pieces ? t("packing.summary.over", "{{n}} packed more than made", { n: version.over_pieces }) : null,
                              version.foreign_pieces ? t("packing.summary.foreign", "{{n}} not in the semi-finished", { n: version.foreign_pieces }) : null,
                            ].filter(Boolean).join(" · ")}
                          </Badge>
                        )}
                      </td>
                      <td className="px-2 py-1.5" onClick={(event) => event.stopPropagation()}>
                        <Switch
                          checked={active}
                          disabled={readOnly || version.is_default || act.isPending}
                          label={active ? t("assembly.version.on", "On") : t("assembly.version.off", "Off")}
                          onChange={(on) => act.mutate(() => client.setActive(version.id, on))}
                        />
                      </td>
                      <td className="px-2 py-1.5" onClick={(event) => event.stopPropagation()}>
                        {!readOnly && (
                          <MenuButton
                            variant="ghost"
                            label={t("assembly.version.more", "More")}
                            items={[
                              {
                                label: t("assembly.version.make-default", "Make it the default"),
                                disabled: version.is_default || !active || !version.is_complete,
                                onClick: () => act.mutate(() => client.makeDefault(version.id)),
                              },
                              {
                                label: t("assembly.version.remove", "Remove the version"),
                                disabled: version.is_default,
                                onClick: () => {
                                  void (async () => {
                                    const ok = await confirm({
                                      title: t("assembly.version.remove.title", "Remove version {{code}}?", { code: version.code }),
                                      message: t("assembly.version.remove.message", "Its boxes and packing list go; its part stays, switched off. A version something was made as cannot be removed: switch it off instead."),
                                      confirmLabel: t("assembly.version.remove.confirm", "Remove"),
                                      variant: "danger",
                                    });
                                    if (ok) act.mutate(() => client.removeVersion(version.id));
                                  })();
                                },
                              },
                            ]}
                          />
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {chosen && structure && (
        <VersionDetail
          key={chosen.id}
          draft={draft}
          structure={structure}
          version={chosen}
          readOnly={readOnly}
          view={view ?? (chosen.is_complete ? "boxes" : "packing")}
          onView={setView}
        />
      )}

      {adding && structure && (
        <AddVersionDialog
          productId={draft.id}
          sku={draft.metadata?.sku ?? ""}
          hasDefault={versions.length > 0}
          onClose={() => setAdding(false)}
          onAdded={async (version) => {
            setAdding(false);
            setChosenId(version.id);
            await refresh();
          }}
        />
      )}
      {asking && <WhichVersionDialog productId={draft.id} onClose={() => setAsking(false)} />}
    </div>
  );
}

type DetailView = "packing" | "boxes" | "bom" | "rule" | "files";

function VersionDetail({
  draft,
  structure,
  version,
  readOnly,
  view,
  onView,
}: {
  draft: ProductDTO;
  structure: ProductStructure;
  version: AssemblyVersionDTO;
  readOnly: boolean;
  view: DetailView;
  onView: (view: DetailView) => void;
}) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const detail = useQuery({
    queryKey: ["manufacturing", "part", "detail", version.output_part_id],
    queryFn: () => new ManufacturingPartClient(http).detail(version.output_part_id),
    enabled: view === "bom",
    staleTime: 60_000,
  });
  const reference = useReference(http, view === "bom");

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-line bg-surface px-4 py-2 lg:px-6">
        <span className="text-[13px] font-semibold">{version.code}</span>
        <span className="font-mono text-[12px] text-ink-muted">{version.output_part_number}</span>
        <span className="mx-1 h-4 w-px bg-line" />
        <Segmented
          value={view}
          onChange={onView}
          options={[
            { value: "packing", label: t("assembly.view.packing", "Pieces by box") },
            { value: "boxes", label: t("assembly.view.boxes", "Boxes") },
            { value: "bom", label: t("assembly.view.bom", "Assembly BOM") },
            { value: "rule", label: t("assembly.view.rule", "Rule") },
            { value: "files", label: t("assembly.view.files", "Files") },
          ]}
        />
      </div>
      {view === "packing" && <PackingGrid version={version} structure={structure} readOnly={readOnly} />}
      {view === "boxes" && <VersionBoxes version={version} readOnly={readOnly} />}
      {view === "bom" &&
        (detail.data ? (
          <PartBomTab
            part={detail.data.part}
            units={reference.units ?? []}
            operations={reference.operations ?? []}
            departments={reference.departments ?? []}
            showRouting={false}
            readOnly={readOnly}
          />
        ) : (
          <p className="px-4 py-3 text-[13px] text-ink-muted lg:px-6">{t("common.loading", "Loading…")}</p>
        ))}
      {view === "rule" && <VersionRule productId={draft.id} version={version} readOnly={readOnly} />}
      {view === "files" && (
        <PartFilesPanel
          parts={[{ id: version.output_part_id, number: version.output_part_number ?? "" }]}
          uploadPartId={null}
          productId={draft.id}
          uploadToProduct
          readOnly={readOnly}
        />
      )}
    </div>
  );
}

/** When a version is used: its rule, its ship SKU, and what the rules would have done with last year's sales. */
function VersionRule({ productId, version, readOnly }: { productId: number; version: AssemblyVersionDTO; readOnly: boolean }) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const queryClient = useQueryClient();
  const client = useMemo(() => new AssemblyVersionClient(http), [http]);
  const profiles = useQuery({ queryKey: ["manufacturing", "version-profile"], queryFn: () => new VersionProfileClient(http).findAll() });
  const preview = useQuery({ queryKey: ["manufacturing", "version-preview", productId], queryFn: () => client.preview(productId, 52) });
  const [shipSku, setShipSku] = useState(version.ship_sku ?? "");
  const save = useMutation({
    mutationFn: (action: () => Promise<void>) => action(),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["manufacturing"] });
      toast.success(t("notification.saved", "Saved"));
    },
    onError: (failure) => toast.error(refusalMessage(failure, t("error.generic", "Something went wrong."))),
  });
  const total = (preview.data ?? []).reduce((sum, row) => sum + Number(row.units), 0);
  const mine = preview.data?.find((row) => row.version_id === version.id);

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-auto px-4 py-4 lg:px-6">
      <div className="grid max-w-3xl gap-3 sm:grid-cols-2">
        <Field label={t("assembly.rule.profile", "Used when")} htmlFor="version-profile">
          {version.is_default ? (
            <p className="text-[13px] text-ink-muted">{t("assembly.rule.default", "The default: it serves whatever no other version's rule takes.")}</p>
          ) : (
            <span className="flex items-center gap-2">
              <Select
                id="version-profile"
                value={version.version_profile_id == null ? "" : String(version.version_profile_id)}
                disabled={readOnly}
                options={[
                  { value: "", label: t("assembly.rule.none", "No rule (never chosen)") },
                  ...(profiles.data ?? []).map((profile) => ({
                    value: String(profile.id),
                    label: profile.is_active ? profile.name : t("assembly.rule.inactive", "{{name}} (off)", { name: profile.name }),
                  })),
                ]}
                onChange={(event) => save.mutate(() => client.setProfile(version.id, event.target.value === "" ? null : Number(event.target.value)))}
              />
              <Link to="/config/manufacturing/version-rule" className="whitespace-nowrap text-[12px] text-accent underline-offset-2 hover:underline">
                {t("assembly.rule.manage", "Edit the rules")}
              </Link>
            </span>
          )}
        </Field>
        <Field
          label={t("assembly.rule.ship-sku", "Ships at the 3PL as")}
          htmlFor="version-ship-sku"
          hint={t("assembly.rule.ship-sku.hint", "What Deringer knows it by. Empty: the product's own SKU.")}
        >
          <Input
            id="version-ship-sku"
            value={shipSku}
            disabled={readOnly}
            placeholder={version.product_sku ?? ""}
            onChange={(event) => setShipSku(event.target.value)}
            onBlur={() => {
              if (shipSku.trim() !== (version.ship_sku ?? "")) save.mutate(() => client.setShipSku(version.id, shipSku.trim() || null));
            }}
          />
        </Field>
      </div>

      <section className="max-w-3xl">
        <h4 className="mb-1 text-[12px] font-semibold uppercase tracking-wide text-ink-muted">
          {t("assembly.rule.preview", "Last 52 weeks by these rules")}
        </h4>
        {preview.isLoading ? (
          <p className="text-[13px] text-ink-muted">{t("common.loading", "Loading…")}</p>
        ) : total === 0 ? (
          <p className="text-[13px] text-ink-muted">{t("assembly.rule.preview.none", "No sales in the last 52 weeks.")}</p>
        ) : (
          <>
            <p className="mb-2 text-[13px]">
              {(preview.data ?? []).map((row) => t("assembly.rule.preview.share", "{{code}}: {{units}} of {{total}}", { code: row.code, units: row.units, total })).join(" · ")}
            </p>
            {mine && mine.clients.length > 0 && (
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wide text-ink-faint">
                    <th className="py-1 font-medium">{t("assembly.rule.preview.client", "Client")}</th>
                    <th className="py-1 font-medium">{t("assembly.rule.preview.market", "Market")}</th>
                    <th className="py-1 text-right font-medium">{t("assembly.rule.preview.units", "Units")}</th>
                  </tr>
                </thead>
                <tbody>
                  {mine.clients.slice(0, 15).map((row) => (
                    <tr key={String(row.client_id)} className="border-t border-line">
                      <td className="py-1">{row.client_name ?? row.client_no ?? ""}</td>
                      <td className="py-1">{row.market}</td>
                      <td className="py-1 text-right tabular-nums">{row.units}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}
      </section>
    </div>
  );
}

/** A new version: a copy of the default (new part 92-sku-code) or a part that exists. */
function AddVersionDialog({
  productId,
  sku,
  hasDefault,
  onClose,
  onAdded,
}: {
  productId: number;
  sku: string;
  hasDefault: boolean;
  onClose: () => void;
  onAdded: (version: AssemblyVersionDTO) => Promise<void>;
}) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const client = useMemo(() => new AssemblyVersionClient(http), [http]);
  const [code, setCode] = useState("");
  const [mode, setMode] = useState<"copy" | "part">(hasDefault ? "copy" : "part");
  const [search, setSearch] = useState("");
  const [partId, setPartId] = useState("");
  const candidates = useQuery({
    queryKey: ["manufacturing", "structure-candidates", "ASSEMBLY_VERSION", search],
    queryFn: () => client.candidates("ASSEMBLY_VERSION", search),
    enabled: mode === "part",
  });
  const valid = /^[A-Za-z0-9][A-Za-z0-9_]{0,15}$/.test(code.trim()) && (mode === "copy" || partId !== "");
  const add = useMutation({
    mutationFn: () => client.addVersion(productId, code.trim().toUpperCase(), mode === "part" ? Number(partId) : null),
    onSuccess: onAdded,
    onError: (failure) => toast.error(refusalMessage(failure, t("error.generic", "Something went wrong."))),
  });
  const number = `92-${sku}-${code.trim().toUpperCase() || "CODE"}`;

  return (
    <Dialog
      open
      onClose={onClose}
      title={t("assembly.version.add", "Add a version")}
      description={t("assembly.version.add.description", "It starts switched off: place the pieces in its boxes, give it a rule, then switch it on.")}
      actions={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("dialog.cancel", "Cancel")}
          </Button>
          <Button variant="primary" disabled={!valid} loading={add.isPending} onClick={() => add.mutate()}>
            {t("assembly.version.add.confirm", "Add")}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <Field label={t("assembly.version.code", "Version")} htmlFor="version-code" hint={t("assembly.version.code.hint", "A short name: US, AMAZON, CA2.")} required>
          <Input id="version-code" value={code} autoFocus onChange={(event) => setCode(event.target.value)} />
        </Field>
        {hasDefault && (
          <Segmented
            value={mode}
            onChange={setMode}
            options={[
              { value: "copy", label: t("assembly.version.add.copy", "A copy of the default") },
              { value: "part", label: t("assembly.version.add.part", "A part that exists") },
            ]}
          />
        )}
        {mode === "copy" ? (
          <p className="text-[13px] text-ink-muted">
            {t("assembly.version.add.copy.hint", "Part {{number}} is made with the default's assembly BOM, boxes and packing list: change only what differs.", { number })}
          </p>
        ) : (
          <>
            <Field label={t("assembly.version.add.search", "Find the part")} htmlFor="version-part-search">
              <Input id="version-part-search" value={search} onChange={(event) => setSearch(event.target.value)} />
            </Field>
            <Select
              aria-label={t("column.part.number", "Part")}
              value={partId}
              placeholder={t("assembly.version.add.pick", "A part of an assembly version group, not used yet")}
              options={(candidates.data ?? []).map((part) => ({ value: String(part.id), label: `${part.number} · ${part.name}` }))}
              onChange={(event) => setPartId(event.target.value)}
            />
          </>
        )}
      </div>
    </Dialog>
  );
}

/** Which version serves a market, a client or a sales channel, and why. */
function WhichVersionDialog({ productId, onClose }: { productId: number; onClose: () => void }) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const client = useMemo(() => new AssemblyVersionClient(http), [http]);
  const [by, setBy] = useState<"market" | "client" | "channel">("market");
  const [value, setValue] = useState("");
  const clients = useQuery({ queryKey: ["client", "active"], queryFn: () => new NexpimClientClient(http).active(), enabled: by === "client" });
  const channels = useQuery({ queryKey: ["sales-channel", "all"], queryFn: () => new SalesChannelClient(http).findAll(), enabled: by === "channel" });
  const answer = useQuery({
    queryKey: ["manufacturing", "version-resolve", productId, by, value],
    queryFn: () =>
      client.resolve(productId, by === "market" ? { market: value } : by === "client" ? { clientId: Number(value) } : { salesChannelId: Number(value) }),
    enabled: value !== "",
  });
  const options =
    by === "market"
      ? MARKETS.map((market) => ({ value: market, label: market }))
      : by === "client"
        ? (clients.data ?? []).map((one) => ({ value: String(one.cli_id), label: clientLabel(one) }))
        : (channels.data ?? []).map((one) => ({ value: String(one.id), label: one.name }));

  return (
    <Dialog
      open
      onClose={onClose}
      title={t("assembly.which.title", "Which version?")}
      description={t("assembly.which.description", "The version a demand gets, by the rules, and why.")}
      actions={
        <Button variant="ghost" onClick={onClose}>
          {t("dialog.close", "Close")}
        </Button>
      }
    >
      <div className="flex flex-col gap-3">
        <Segmented
          value={by}
          onChange={(next) => {
            setBy(next);
            setValue("");
          }}
          options={[
            { value: "market", label: t("assembly.which.market", "A market") },
            { value: "client", label: t("assembly.which.client", "A client") },
            { value: "channel", label: t("assembly.which.channel", "A sales channel") },
          ]}
        />
        <Select aria-label={t("assembly.which.pick", "Pick one")} value={value} placeholder={t("assembly.which.pick", "Pick one")} options={options} onChange={(event) => setValue(event.target.value)} />
        {answer.data && (
          <div className="rounded-md border border-line bg-surface-sunk px-3 py-2 text-[13px]">
            <p>
              <span className="font-semibold">{answer.data.code}</span>
              {answer.data.ship_sku ? <span className="ml-2 text-ink-muted">{t("assembly.which.ships", "ships as {{sku}}", { sku: answer.data.ship_sku })}</span> : null}
            </p>
            <p className="text-ink-muted">{answer.data.reason}</p>
            {answer.data.substitution === "PREFERRED" && (
              <p className="text-ink-muted">{t("assembly.which.preferred", "Preferred: the default when it has none in stock.")}</p>
            )}
          </div>
        )}
      </div>
    </Dialog>
  );
}
